import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useBbox, useComponentProps} from 'helpers/hooks/utils';
import Typography, {Span} from 'components/atoms/Text/Typography/Typography';
import Box from 'components/atoms/Layout/Box/Box';
import utils from 'helpers/utils';
import StyledInsightCard from 'components/molecules/Cards/InsightCard/InsightCard.styles';
import Link from 'components/atoms/Links/Link/Link';
import Icon from 'components/atoms/Icons/Icon/Icon';
import constants from 'helpers/constants';
import {EventNote} from '@mui/icons-material';
import Markdown from 'components/atoms/Formatters/Markdown/Markdown';
import ActionChip from 'components/molecules/Chips/ActionChip/ActionChip';
import {useInsightLocation} from 'services/client/insight/insight.utils';
import {useLinkNavigate} from 'helpers/hooks/links';

const InsightCard = React.forwardRef((props, ref) => {
  const {
    insight,
    isLoading,
    onClick,
    ...innerProps
  } = useComponentProps(props, 'InsightCard', {
    static: ['isLoading', 'clampText']
  });

  const innerRef = useRef(null);
  const contentRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  const contentBox = useBbox(() => contentRef.current?.querySelector('.InsightCard-text'), ['clamped']);

  const [clampText, setClampText] = useState(true);
  const [clampActive, setClampActive] = useState(false);

  const navigate = useLinkNavigate();
  const insightLocation = useInsightLocation(insight);

  useEffect(() => {
    if (clampText) {
      setClampActive(contentBox?.clamped);
    }
  }, [contentBox?.clamped, clampText]);

  const renderTitle = () => {
    return <Box className="InsightCard-title">
      <Typography showTooltip={true}
                  isLoading={isLoading}
                  variant="subtitle1">
        {insight?.title}
      </Typography>
    </Box>
  }

  const renderText = () => {
    const text = insight?.embed || insight?.text;
    if (text) {
      return <Box className="InsightCard-text">
        <Typography as="div" isLoading={isLoading} variant="body2">
          <Markdown disabled={clampText}>{text}</Markdown>
        </Typography>
      </Box>
    } else if (isLoading) {
      return <Box className="InsightCard-text">
        <Typography isLoading={isLoading} variant="body2" min={16} max={32}/>
      </Box>
    }
  }

  const renderDate = () => {
    if (insight?.createdAt) {
      return <Typography className="InsightCard-date"
                         isLoading={isLoading}
                         variant="caption" color="text.secondary">
        <Icon size="smaller" icon={EventNote}/>
        <Span showTooltip={true}>{utils.dayjs(new Date(insight?.createdAt)).format('MMM YY')}</Span>
      </Typography>
    }
  }

  const renderLocation = () => {
    const to = insightLocation.value === constants.insight.locations.collection ?
      `/collections/${insight.collection.collectionId}/entities` : `/${insightLocation.value}`;

    const locationAction = {
      label: insightLocation.label,
      navigation: {
        to: to,
        resetSearchParams: true
      },
      icon: insightLocation.icon,
      ChipProps: {
        selected: false
      }
    };

    return <ActionChip className="InsightCard-location"
                       action={locationAction}
                       variant="outlined"
                       color="primary"
                       size={'small'}
                       isLoading={isLoading} />
  }

  const handleClick = (e) => {
    onClick?.(e, insight);
    if (!e.defaultPrevented) {
      const entity = (insight.entities?.length > 0) ? (insight.entities?.length > 1 ? {entityId: 0} : insight.entities?.[0]) : null;

      let param = `?custom=insight:${+insight.insightId}`;

      if (insightLocation?.value === constants.insight.locations.collection) {
        navigate({
          event: e,
          to: `/collections/${insight.collection.collectionId}/entities${entity ? `/${entity.entityId}` : ''}${param}`,
          keepSearchParams: true
        });
      } else if (insightLocation?.value === constants.insight.locations.dealflow) {
        navigate({
          event: e,
          to: `/dealflow${entity ? `/${entity.entityId}` : '/table'}${param}`,
          keepSearchParams: true
        });
      } else if (insightLocation?.value === constants.insight.locations.dealflow) {
        navigate({
          event: e,
          to: `/database${entity ? `/${entity.entityId}` : ''}${param}`,
          keepSearchParams: true
        });
      }
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className, {
    clampText: clampText
  });

  return <StyledInsightCard ref={innerRef} {...innerProps}
                            isLoading={isLoading}
                            variant="outlined"
                            onClick={handleClick}>
    <Box ref={contentRef} className="InsightCard-content">
      {renderTitle()}
      <Box className="InsightCard-text-wrapper">
        {renderText()}
        {clampActive ? <Link className="readMore"
                             isLoading={isLoading}
                             onMouseDown={(e) => e.stopPropagation()}
                             onClick={(e) => {
                               setClampText((current) => !current);
                               e.stopPropagation();
                               e.preventDefault();
                             }}>
          Show {clampText ? 'more' : 'less'}
        </Link> : null}
      </Box>
    </Box>

    {!isLoading ? <Box className="InsightCard-footer">
      {renderDate()}
      {renderLocation()}
    </Box> : null}
  </StyledInsightCard>
});

InsightCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  article: PropTypes.object,
  isLoading: PropTypes.bool,
  onClick: PropTypes.func
};

export default InsightCard;
